// frontend/src/components/ChasseHuntMap.jsx
//
// Composant Leaflet partagé pour les pages cartographiques de l'app
// (AuctionDetail, CommunityMap, EnregistrerTerre, MyHunts, PersonalMap,
// TerresDisponibles — qui inclut désormais aussi la couche zones de
// chasse, autrefois portée par une page autonome).
//
// Avant ce composant chaque page reproduisait :
//   - le même boilerplate <MapContainer><TileLayer>
//   - le même workaround d'icône par défaut Leaflet (icon URL cassé en
//     bundling Webpack/Vite)
//   - les mêmes helpers FitBounds / SetView / MapReady avec useMap()
//
// L'API est volontairement plate : un seul composant qui couvre 95 % des
// cas (markers, popups, GeoJSON, cluster, fitBounds, flyTo) et un slot
// `children` comme escape hatch pour les pages qui ont besoin d'injecter
// des couches non-standard (EditControl pour le dessin sur
// EnregistrerTerre, par exemple).
//
// Toute évolution future (offline tiles, drawer mode, polygon-search…)
// doit passer par ce composant — on évite de re-créer du boilerplate
// dans chaque page.

import React, { useEffect, useMemo, useRef } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  GeoJSON,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// ── Workaround icône par défaut Leaflet ──────────────────────────────────────
//
// Webpack/Vite ne résolvent pas correctement les URLs internes du package
// `leaflet` (marker-icon.png, marker-shadow.png) — le bundle pointe vers
// des chemins relatifs cassés. On force des URLs CDN une seule fois au
// chargement du module. Sans ça, les marqueurs par défaut ne s'affichent
// pas du tout. Toutes les pages qui rendaient elles-mêmes ce fix peuvent
// désormais le supprimer dès qu'elles passent par <ChasseHuntMap />.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// ── Tile providers ───────────────────────────────────────────────────────────
//
// Centralisés ici pour qu'une page n'ait jamais à se souvenir de l'URL
// exacte d'un provider ou de son attribution légale. Pour ajouter un
// basemap : étendre cet objet — pas besoin de toucher les pages
// consommatrices.
const BASEMAPS = {
  osm: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution:
      'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    maxZoom: 19,
  },
  topo: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution:
      'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, SRTM | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
    maxZoom: 17,
  },
  'cartodb-dark': {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    maxZoom: 19,
  },
};

// ── Helpers internes (utilisent useMap()) ────────────────────────────────────
//
// Ces sous-composants doivent obligatoirement vivre à l'intérieur d'un
// <MapContainer>, car `useMap()` lit le contexte de Leaflet. Ils sont
// montés conditionnellement par <ChasseHuntMap> selon les props.

function FitToMarkers({ markers }) {
  const map = useMap();
  useEffect(() => {
    if (!markers || markers.length === 0) return;
    const bounds = L.latLngBounds(markers.map((m) => [m.lat, m.lng]));
    // padding = laisser un peu de marge autour des points extrêmes pour
    // que les marqueurs ne soient pas collés aux bords du viewport.
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [markers, map]);
  return null;
}

// Variante `FitToMarkers` mais bornes explicites — utile quand on veut
// fitter sur l'étendue d'un GeoJSON ou d'une province plutôt que sur des
// markers. JSON.stringify pour la comparaison : Leaflet n'expose pas
// d'égalité structurelle sur LatLngBounds et le parent peut passer une
// nouvelle référence à chaque render même pour des bornes identiques.
function FitToBounds({ bounds }) {
  const map = useMap();
  const prevKey = useRef(null);
  useEffect(() => {
    if (!bounds) return;
    const key = JSON.stringify(bounds);
    if (key === prevKey.current) return;
    prevKey.current = key;
    map.fitBounds(bounds, { padding: [40, 40], animate: true, duration: 0.6 });
  }, [bounds, map]);
  return null;
}

function FlyToCenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (!center) return;
    map.setView(center, zoom ?? map.getZoom(), { animate: true });
  }, [center, zoom, map]);
  return null;
}

function MapReadyEmitter({ onReady }) {
  const map = useMap();
  useEffect(() => {
    if (typeof onReady === 'function') onReady(map);
    // onReady volontairement omis : on émet une seule fois au mount,
    // sinon un parent qui re-crée la callback à chaque render déclencherait
    // une cascade infinie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  return null;
}

function MapClickHandler({ onClick }) {
  useMapEvents({
    click: (e) => {
      if (typeof onClick === 'function') onClick(e.latlng);
    },
  });
  return null;
}

// Ajoute une mention dans l'attribution control Leaflet — utilisé pour
// satisfaire les clauses de droit d'auteur des jeux de données géographiques
// affichés (ex: « © Gouvernement du Québec » imposé par la licence MELCCFP).
// Le composant se monte / démonte avec la couche, donc l'attribution
// disparaît automatiquement quand la donnée n'est plus à l'écran.
export function DataAttribution({ text }) {
  const map = useMap();
  useEffect(() => {
    if (!text) return undefined;
    map.attributionControl.addAttribution(text);
    return () => {
      map.attributionControl.removeAttribution(text);
    };
  }, [text, map]);
  return null;
}

// ── <ChasseHuntMap /> ────────────────────────────────────────────────────────

export default function ChasseHuntMap({
  center = [52, -72],
  zoom = 6,
  minZoom,
  maxZoom,
  height = '500px',
  style,
  className,
  basemap = 'osm',
  markers,
  geoJSON,
  geoJSONOptions,
  cluster = false,
  clusterOptions,
  fitToMarkers = false,
  fitBounds,
  flyToCenter = false,
  scrollWheelZoom = true,
  zoomControl = true,
  dragging = true,
  onMapReady,
  onClick,
  children,
}) {
  // basemap=null désactive complètement le TileLayer par défaut — utile
  // pour EnregistrerTerre qui veut switcher TileLayer / WMSTileLayer à
  // l'exécution via les children.
  const tile = basemap === null ? null : BASEMAPS[basemap] || BASEMAPS.osm;

  // On rend les marqueurs même quand `markers` est vide pour ne pas
  // démonter/remonter <MarkerClusterGroup> à chaque changement (le
  // cluster a un état d'expansion interne qu'on veut préserver).
  const markersFragment = useMemo(() => {
    if (!markers || markers.length === 0) return null;
    return markers.map((m) => (
      <Marker
        key={m.id ?? `${m.lat},${m.lng}`}
        position={[m.lat, m.lng]}
        icon={m.icon}
        draggable={!!m.draggable}
        eventHandlers={m.eventHandlers}
      >
        {m.popup ? <Popup>{m.popup}</Popup> : null}
      </Marker>
    ));
  }, [markers]);

  const containerStyle = { height, width: '100%', ...style };

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      minZoom={minZoom}
      maxZoom={maxZoom}
      scrollWheelZoom={scrollWheelZoom}
      zoomControl={zoomControl}
      dragging={dragging}
      style={containerStyle}
      className={className}
    >
      {tile ? (
        <TileLayer url={tile.url} attribution={tile.attribution} maxZoom={tile.maxZoom} />
      ) : null}

      {geoJSON ? <GeoJSON data={geoJSON} {...(geoJSONOptions || {})} /> : null}

      {cluster ? (
        <MarkerClusterGroup {...(clusterOptions || {})}>{markersFragment}</MarkerClusterGroup>
      ) : (
        markersFragment
      )}

      {fitToMarkers ? <FitToMarkers markers={markers} /> : null}
      {fitBounds ? <FitToBounds bounds={fitBounds} /> : null}
      {flyToCenter ? <FlyToCenter center={center} zoom={zoom} /> : null}
      {onMapReady ? <MapReadyEmitter onReady={onMapReady} /> : null}
      {onClick ? <MapClickHandler onClick={onClick} /> : null}

      {children}
    </MapContainer>
  );
}

// ── Helpers exportés ────────────────────────────────────────────────────────
//
// Pratiques pour les pages qui veulent construire un icône custom sans
// importer `leaflet` directement.
export function createDivIcon({
  html,
  iconSize = [26, 38],
  iconAnchor = [13, 38],
  className = '',
}) {
  return L.divIcon({ html, iconSize, iconAnchor, className });
}

// Re-export du `L` brut pour les rares cas où une page a besoin d'une
// API Leaflet exotique non couverte par les helpers ci-dessus
// (latLngBounds, marqueurs imagés, événements de calque, etc.).
export { L };
